
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface StripePaymentParams {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  serviceType: string;
  serviceTier: string;
  price: number;
  projectDescription?: string;
  successUrl?: string;
  cancelUrl?: string;
}

export const useStripe = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const createPayment = async (params: StripePaymentParams) => {
    console.log('🔵 [Stripe] Starting payment creation with params:', {
      customerName: params.customerName,
      customerEmail: params.customerEmail,
      serviceType: params.serviceType,
      serviceTier: params.serviceTier,
      price: params.price,
      successUrl: params.successUrl,
      cancelUrl: params.cancelUrl,
    });

    try {
      setIsLoading(true);

      console.log('🔵 [Stripe] Invoking Supabase function: create-stripe-payment');

      const { data, error } = await supabase.functions.invoke('create-stripe-payment', {
        body: {
          customerName: params.customerName,
          customerEmail: params.customerEmail,
          customerPhone: params.customerPhone,
          serviceType: params.serviceType,
          serviceTier: params.serviceTier,
          price: params.price,
          projectDescription: params.projectDescription || '',
          successUrl: params.successUrl,
          cancelUrl: params.cancelUrl,
        },
      });

      console.log('🔵 [Stripe] Supabase function response:', { data, error });

      if (error) {
        console.error('🔴 [Stripe] Supabase function error:', error);
        throw error;
      }

      // Redirect to Stripe checkout
      if (data?.url) {
        console.log('🟢 [Stripe] Redirecting to Stripe checkout:', data.url);
        window.location.href = data.url;
        return { success: true };
      } else {
        console.error('🔴 [Stripe] No checkout URL in response:', data);
        throw new Error('No checkout URL received from server');
      }
    } catch (error: any) {
      console.error('🔴 [Stripe] Payment creation error:', error);
      toast({
        title: "Payment Error",
        description: error.message || "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPayment = async (sessionId: string) => {
    try {
      setIsLoading(true);

      const { data, error } = await supabase.functions.invoke('verify-stripe-payment', {
        body: { sessionId },
      });

      if (error) {
        throw error;
      }

      return {
        success: data.paymentStatus === 'paid',
        paymentStatus: data.paymentStatus,
        customerEmail: data.customerEmail
      };
    } catch (error: any) {
      toast({
        title: "Verification Error",
        description: error.message || "Failed to verify payment status.",
        variant: "destructive",
      });
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createPayment,
    verifyPayment,
    isLoading
  };
};
